// Sidebar screens returned by the global search ("navigationlocation" results).
//
// The API identifies a screen by its navigation key: the trimmed menuItemName of every
// sidebar node from the module down to the screen, joined by NAVIGATION_KEY_SEPARATOR
// (e.g. "Billing > Reports Menu > T2202 Form"). The Tools service builds the same key from
// the same SidebarItemData.json, so the UI can find the menu item in its own sidebar tree,
// label it with its own translations and open it exactly as a sidebar click would.

export const NAVIGATION_ENTITY_TYPE = 'navigationlocation';
export const NAVIGATION_KEY_SEPARATOR = ' > ';

// History state key telling the Sidebar which leaf to highlight when several leaves share
// the destination url (e.g. every "student-search" entry point).
export const GLOBAL_SEARCH_NAV_STATE_KEY = 'globalSearchNavKey';

const trimmedName = (item) => (typeof item?.menuItemName === 'string' ? item.menuItemName.trim() : '');

/** Navigation key of a sidebar path (array of sidebar items, module first). */
export const buildNavigationKey = (path) => (path || []).map(trimmedName).join(NAVIGATION_KEY_SEPARATOR);

/**
 * Finds the sidebar item a navigation key points to.
 * @returns {{ path: Array, indices: number[] } | null} the items from the module down to the
 *          screen and their indices in the tree, or null when the key matches no leaf.
 */
export function findNavigationLocation(sideBarItems, navigationKey) {
    if (!Array.isArray(sideBarItems) || typeof navigationKey !== 'string' || navigationKey.length === 0) {
        return null;
    }
    const names = navigationKey.split(NAVIGATION_KEY_SEPARATOR);

    let level = sideBarItems;
    const path = [];
    const indices = [];
    for (let depth = 0; depth < names.length; depth++) {
        if (!Array.isArray(level)) return null;
        const index = level.findIndex((item) => trimmedName(item) === names[depth]);
        if (index < 0) return null;
        path.push(level[index]);
        indices.push(index);
        level = level[index].subLinks;
    }

    const leaf = path[path.length - 1];
    if (Array.isArray(leaf.subLinks) && leaf.subLinks.length > 0) return null;
    return { path, indices };
}

/**
 * Opens a sidebar screen the way the Sidebar's leaf click handlers do: stores the item's
 * redirect (the page the intermediate search page forwards to) and pushes its url. The key
 * rides in the history state so the Sidebar expands and highlights this exact leaf.
 */
export function openNavigationLocation(history, leaf, navigationKey) {
    if (!leaf || typeof leaf.url !== 'string' || !leaf.url.startsWith('/')) return false;

    document.getElementsByTagName('html')[0].classList.remove('popup-Page');
    if (leaf.redirect) {
        localStorage.setItem('redirectUrl', leaf.redirect);
    }
    history.push(leaf.url, { [GLOBAL_SEARCH_NAV_STATE_KEY]: navigationKey });
    return true;
}
